import translate from '@translate';
import React, { FC, useEffect, useMemo, useState } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { TransportLinkedOrderType, TransportType } from 'src/types/transport';
import { OnChangeMultipleType } from '.';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Typography from 'src/components/Typography';
import { UserServiceCode } from 'src/types/user';
import Switch from 'src/components/Switch';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import Tooltip from 'src/components/Tooltip';
import { IconButton } from '@mui/material';
import { ExportIcon, FileCountryIcon } from 'src/components/SkladonIcon/SmallIcons';
import { sizes } from 'src/themes/default';
import { checkPermission } from 'src/utils/checkPermission';
import { filterTransportServiceCodes, filterTransportShipperCodes, withCurrentOption } from './functions';
import EgonBadge from 'src/components/EgonBadge';
import { useCheckPermission } from 'src/utils/checkPermission';

export interface TransportPropsType {
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    editData: TransportType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    permissionEditAll: boolean;
    shipperCodes: ConstantsCodeType[];
    serviceCodes: ConstantsCodeType[];
    providerServiceCodes: UserServiceCode[];
    newTransport?: boolean;
    handleShowShipperLabel?: () => void;
    handleDownloadShipperLabel?: () => void;
    requestPickupDate?: (shipper: string) => void;
    summary?: boolean;
    linkedOrders?: TransportLinkedOrderType[];
    skladonUser?: number;
}

const Transport: FC<TransportPropsType> = (props) => {
    const permissionEgonView = useCheckPermission('EGON.VIEW');
    const showEgonBadge = !!props.skladonUser && permissionEgonView && props.editData.CARRIER_BACKEND === 'egon';
    const [shipperCodes, setShipperCodes] = useState<ConstantsCodeType[]>([]);
    const [serviceCodes, setServiceCodes] = useState<ConstantsCodeType[]>([]);
    const shipperFromOrder = !props.newTransport && (props.linkedOrders?.length ?? 0) > 0;

    // Only carriers that have a pallet service for this transport TYPE on the provider.
    useEffect(() => {
        if (props.shipperCodes && props.providerServiceCodes) {
            setShipperCodes(
                filterTransportShipperCodes(props.shipperCodes, props.providerServiceCodes, props.editData.TYPE)
            );
        } else {
            setShipperCodes([]);
        }
    }, [props.shipperCodes, props.providerServiceCodes, props.editData.TYPE]);

    // Only pallet services assigned to the provider for the selected shipper + TYPE.
    useEffect(() => {
        if (!props.editData.SHIPPER || !shipperCodes.length) {
            setServiceCodes([]);
            return;
        }

        setServiceCodes(
            filterTransportServiceCodes(
                props.serviceCodes,
                props.providerServiceCodes,
                props.editData.SHIPPER,
                props.editData.TYPE
            )
        );
    }, [props.editData.SHIPPER, props.editData.TYPE, shipperCodes, props.serviceCodes, props.providerServiceCodes]);

    // Auto-fill only an empty service; never overwrite or clear a stored value.
    useEffect(() => {
        if (shipperFromOrder || props.disableEdit || props.summary) {
            return;
        }
        if (serviceCodes.length === 1 && !props.editData.SHIPPER_SERVICE_CODE) {
            props.onChange('SHIPPER_SERVICE_CODE', serviceCodes[0].value, false);
        }
    }, [serviceCodes, props.editData.SHIPPER_SERVICE_CODE, props.disableEdit, props.summary, shipperFromOrder]);

    // Keep the stored value displayable even when it is not among the allowed (filtered) options.
    const shipperOptions = useMemo(
        () => withCurrentOption(shipperCodes, props.shipperCodes || [], props.editData.SHIPPER),
        [shipperCodes, props.shipperCodes, props.editData.SHIPPER]
    );
    const serviceOptions = useMemo(
        () => withCurrentOption(serviceCodes, props.serviceCodes || [], props.editData.SHIPPER_SERVICE_CODE),
        [serviceCodes, props.serviceCodes, props.editData.SHIPPER_SERVICE_CODE]
    );

    const onChangeShipper = (value: unknown) => {
        const shipper = value as string;
        const newData: TransportType = {
            SHIPPER: shipper,
            SHIPPER_SERVICE_CODE: undefined,
        };

        if (shipper === 'VLASTNI') {
            newData.TRACKING_NUMBER = 'vlastni';
        }

        props.onChangeMultiple(newData);

        if (shipper === 'GEIS') {
            props.requestPickupDate?.(shipper);
        }
    };

    const shipperFromOrderHint = shipperFromOrder ? translate('transportDetail.shipperFromOrderHint') : undefined;

    const canShowLabel =
        !props.newTransport &&
        !props.summary &&
        checkPermission('TRANSPORT.LABEL.SHOW') &&
        !!props.editData.TRACKING_NUMBER;

    const trackingFieldWidthOffset = canShowLabel ? 92 : 0;

    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant="h3" gutterBottom>
                    {translate('transportDetail.transport')}
                    {showEgonBadge && (
                        <span style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                            <EgonBadge
                                orderId={props.editData.EGON_ORDER_ID}
                                statusName={props.editData.EGON_STATUS_NAME}
                                statusAt={props.editData.EGON_STATUS_AT}
                                data-testid="transport-egon-badge"
                            />
                        </span>
                    )}
                </Typography>
            </GridItem>
            <GridItem cols={6}>
                <Select
                    options={shipperOptions}
                    editableOptions={shipperCodes.filter((option): boolean => {
                        if (option.value === 'notSupported' && props.editData.SHIPPER !== 'notSupported') return false;
                        if (option.value === 'CUSTOM' && props.editData.SHIPPER !== 'CUSTOM') return false;
                        return true;
                    })}
                    value={props.editData.SHIPPER}
                    error={props.editErrors.SHIPPER}
                    onChange={onChangeShipper}
                    name="SHIPPER"
                    label={translate('transportDetail.shipper')}
                    required
                    fullWidth
                    disabled={props.disableEdit || props.summary || shipperFromOrder}
                    helperText={shipperFromOrderHint}
                />
            </GridItem>
            <GridItem cols={6}>
                <Select
                    options={serviceOptions}
                    editableOptions={serviceCodes}
                    value={props.editData.SHIPPER_SERVICE_CODE}
                    error={props.editErrors.SHIPPER_SERVICE_CODE}
                    onChange={(value, error) => props.onChange('SHIPPER_SERVICE_CODE', value, error)}
                    name="SHIPPER_SERVICE_CODE"
                    label={translate('transportDetail.shipperService')}
                    required
                    fullWidth
                    disabled={props.disableEdit || props.summary || shipperFromOrder}
                    helperText={shipperFromOrderHint}
                />
            </GridItem>
            <GridItem cols={4}>
                <TextField
                    value={props.editData.ORDER_BOX_COUNT}
                    error={props.editErrors.ORDER_BOX_COUNT}
                    onChange={(value, error) => props.onChange('ORDER_BOX_COUNT', value || null, error)}
                    name="ORDER_BOX_COUNT"
                    label={translate('transportDetail.boxCount')}
                    fullWidth
                    type="numberLocale"
                    min={1}
                    step="1"
                    disabled={props.disableEdit || props.summary}
                    clearValueUndefined
                />
            </GridItem>
            <GridItem cols={4}>
                <TextField
                    value={props.editData.WEIGHT}
                    error={props.editErrors.WEIGHT}
                    onChange={(value, error) => props.onChange('WEIGHT', value, error)}
                    name="WEIGHT"
                    label={translate('transportDetail.weight')}
                    fullWidth
                    multiline
                    disabled={props.disableEdit || props.summary}
                    type="numberLocale"
                    min={0}
                />
            </GridItem>
            <GridItem cols={4}>
                <TextField
                    value={props.editData.TRACKING_NUMBER}
                    error={props.editErrors.TRACKING_NUMBER}
                    onChange={(value, error) => props.onChange('TRACKING_NUMBER', value, error)}
                    name="TRACKING_NUMBER"
                    label={translate('transportDetail.trackingNumber')}
                    fullWidth
                    disabled={props.disableEdit || props.summary}
                    sx={trackingFieldWidthOffset ? { width: `calc(100% - ${trackingFieldWidthOffset}px)` } : undefined}
                />
                {canShowLabel && (
                    <>
                        <Tooltip title={translate('transportDetail.shipperLabel')}>
                            <IconButton
                                onClick={() => props.handleShowShipperLabel?.()}
                                size="small"
                                data-testid="transport-show-label-btn"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size,
                                    },
                                }}
                            >
                                <FileCountryIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={translate('transportDetail.shipperLabelDownload')}>
                            <IconButton
                                onClick={() => props.handleDownloadShipperLabel?.()}
                                size="small"
                                data-testid="transport-download-label-btn"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size,
                                    },
                                }}
                            >
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </GridItem>
            <GridItem cols={12}>
                <AutocompleteTextField
                    value={props.editData.MESSAGE}
                    error={props.editErrors.MESSAGE}
                    onChange={(value, error) => props.onChange('MESSAGE', value || '', error)}
                    name="MESSAGE"
                    label={translate('transportDetail.message')}
                    fullWidth
                    multiline
                    disabled={props.disableEdit || props.summary}
                    autoComplete="TRANSPORT_MESSAGE"
                />
            </GridItem>
            <GridItem cols={6}>
                <Switch
                    value={!!props.editData.TRANSPORT_PAID_BY_SKLADON}
                    onChange={(value) => props.onChange('TRANSPORT_PAID_BY_SKLADON', value ? 1 : 0, false)}
                    label={translate('transportDetail.transportPaidBySkladon')}
                    disabled={!props.permissionEditAll || props.summary}
                />
            </GridItem>
            <GridItem cols={6}>
                <Switch
                    value={!!props.editData.SHIPMENT_PENDING}
                    disabled
                    label={translate('transportDetail.shipmentPending')}
                />
            </GridItem>
        </Grid>
    );
};

export default Transport;
